/**
 * Backfill certification (Layer 5, Task 65).
 *
 * A historical "backfill" batch must be certified before it can be submitted.
 * There is no GET for the flag and `BatchMetadata` doesn't carry it, so this
 * panel reflects the most recent mutation response (cached under
 * queryKeys.batches.backfill for the session) and treats the unknown-on-load
 * state gracefully. Reviewer/admin only; X-Reason required.
 */

import { Badge, Button, Group, Paper, Stack, Text, ThemeIcon } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCertificate, IconHistoryToggle } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useCertifyBackfill, useMarkBackfill } from "../../api/hooks";
import type { BackfillStatus, BatchDetail } from "../../api/types";
import { useHasRole } from "../../auth";
import { queryKeys } from "../../lib/queryKeys";
import { ReasonModal } from "../ReasonModal";

type Pending = "mark" | "certify" | null;

export function BackfillPanel({ batch }: { batch: BatchDetail }) {
  const canManage = useHasRole("reviewer", "admin");
  const queryClient = useQueryClient();
  const mark = useMarkBackfill();
  const certify = useCertifyBackfill();
  const [pending, setPending] = useState<Pending>(null);

  // No GET endpoint — seed from cache (set by a prior mutation this session).
  const [status, setStatus] = useState<BackfillStatus | null>(
    () =>
      queryClient.getQueryData<BackfillStatus>(queryKeys.batches.backfill(batch.batch_id)) ?? null,
  );

  const persist = (s: BackfillStatus) => {
    setStatus(s);
    queryClient.setQueryData(queryKeys.batches.backfill(batch.batch_id), s);
  };

  const activeMutation = pending === "certify" ? certify : mark;

  const onConfirm = async (reason: string) => {
    if (!pending) return;
    try {
      const result = await activeMutation.mutateAsync({ batchId: batch.batch_id, reason });
      persist(result);
      notifications.show({
        title: pending === "certify" ? "Backfill certified" : "Marked as backfill",
        message: batch.batch_id,
        color: "teal",
        autoClose: 3500,
      });
      setPending(null);
    } catch {
      // Surfaced inline in the modal via the mutation's error.
    }
  };

  const isBackfill = status?.is_backfill ?? false;
  const certified = status?.certified ?? false;

  return (
    <>
      <Paper withBorder radius="md" p="md">
        <Group justify="space-between" wrap="wrap" gap="md">
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon variant="light" color="orange" radius="md" size={34}>
              <IconHistoryToggle size={18} />
            </ThemeIcon>
            <Stack gap={2}>
              <Group gap="xs">
                <Text size="sm" fw={600}>
                  Backfill certification
                </Text>
                {status === null ? (
                  <Badge variant="default" size="xs" color="gray">
                    status unknown
                  </Badge>
                ) : certified ? (
                  <Badge variant="light" color="teal" size="xs">
                    certified
                  </Badge>
                ) : isBackfill ? (
                  <Badge variant="light" color="orange" size="xs">
                    marked · not certified
                  </Badge>
                ) : (
                  <Badge variant="light" color="gray" size="xs">
                    not a backfill
                  </Badge>
                )}
              </Group>
              <Text size="xs" c="dimmed" maw={520}>
                Historical backfill batches must be certified before they can be submitted. There's
                no read endpoint, so this reflects actions taken this session.
              </Text>
            </Stack>
          </Group>

          {canManage ? (
            <Group gap="xs">
              <Button
                size="xs"
                variant="default"
                leftSection={<IconHistoryToggle size={14} />}
                onClick={() => setPending("mark")}
                loading={mark.isPending}
              >
                Mark as backfill
              </Button>
              <Button
                size="xs"
                color="teal"
                leftSection={<IconCertificate size={14} />}
                onClick={() => setPending("certify")}
                loading={certify.isPending}
                disabled={certified}
              >
                Certify
              </Button>
            </Group>
          ) : (
            <Text size="xs" c="dimmed">
              Reviewer / admin only.
            </Text>
          )}
        </Group>
      </Paper>

      <ReasonModal
        opened={pending !== null}
        title={pending === "certify" ? "Certify backfill" : "Mark as backfill"}
        description={
          pending === "certify"
            ? `Certify ${batch.batch_id} as a reviewed historical backfill — required before it can be submitted.`
            : `Flag ${batch.batch_id} as a historical backfill batch.`
        }
        confirmLabel={pending === "certify" ? "Certify" : "Mark"}
        confirmColor={pending === "certify" ? "teal" : "orange"}
        busy={activeMutation.isPending}
        error={activeMutation.error}
        onConfirm={onConfirm}
        onClose={() => {
          if (activeMutation.isPending) return;
          setPending(null);
          mark.reset();
          certify.reset();
        }}
      />
    </>
  );
}
